// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { SEOProps, SEOQueryTypes } from 'src/models/seo';

function SEO({
  description = '', lang = 'en', locales = [], meta = [], title = '', keywords = '', image = '',
}: SEOProps) {
  const { site } = useStaticQuery<SEOQueryTypes>(SEOStaticQuery);
  const { pathname } = useLocation();
  const { siteUrl } = site.siteMetadata;
  const seoLocales = locales.map((locale) => ({
    rel: 'alternate',
    hrefLang: locale.lang,
    href: `${siteUrl}${locale.slug}`,
  }));

  const seo = {
    title: title || site.siteMetadata?.title,
    description: description || site.siteMetadata?.description,
    image: image || `${siteUrl}/images${site.siteMetadata?.image}`,
    url: `${siteUrl}${pathname}`,
    keywords: keywords || site.siteMetadata?.keywords,
    author: site.siteMetadata?.author || '',
    twitterSite: site.siteMetadata?.twitterSite || '',
    titleTemplate: site.siteMetadata?.titleTemplate || '',
  };
  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={seo.title}
      titleTemplate={seo.titleTemplate}
      link={seoLocales}
      meta={[
        {
          name: 'description',
          content: seo.description,
        },
        {
          name: 'keywords',
          content: seo.keywords,
        },
        {
          property: 'og:title',
          content: seo.title,
        },
        {
          property: 'og:description',
          content: seo.description,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:url',
          content: seo.url,
        },
        {
          property: 'og:image',
          content: seo.image,
        },
        {
          name: 'twitter:site',
          content: seo.twitterSite,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: seo.image,
        },
        {
          name: 'twitter:creator',
          content: seo.author,
        },
        {
          name: 'twitter:title',
          content: seo.title,
        },
        {
          name: 'twitter:description',
          content: seo.description,
        },
      ].concat(meta)}
    />
  );
}

export default SEO;

const SEOStaticQuery = graphql`
  query {
    site {
      siteMetadata {
        title
        titleTemplate
        keywords
        description
        siteUrl
        image
        author
        twitterSite
      }
    }
  }
`;
